import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DriverBottomNav from "@/components/DriverBottomNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  DollarSign, 
  TrendingUp, 
  Calendar,
  Clock,
  Car,
  Package,
  FileText,
  ChevronRight,
  Download,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Loader2,
  MapPin
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

import { API } from "@/lib/api";
import DriverTaxCenter from "@/components/DriverTaxCenter";


const SERVICE_LABELS = {
  ride: { name: "Rides", icon: Car, color: "text-blue-400", bg: "bg-blue-500/10" },
  envelope: { name: "Envelopes", icon: FileText, color: "text-[#4D88FF]", bg: "bg-[#0047FF]/10" },
  small_package: { name: "Small Packages", icon: Package, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  medium_package: { name: "Medium Packages", icon: Package, color: "text-purple-400", bg: "bg-purple-500/10" },
  delivery: { name: "Deliveries", icon: Package, color: "text-emerald-400", bg: "bg-emerald-500/10" }
};

export default function DriverEarnings() {
  const navigate = useNavigate();
  const { user, isDriver, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("earnings");
  const [earnings, setEarnings] = useState(null);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [availableDeliveries, setAvailableDeliveries] = useState([]);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [period, setPeriod] = useState("week");

  const [taxYears, setTaxYears] = useState([]);
  const [selectedTaxYear, setSelectedTaxYear] = useState(new Date().getFullYear());

  const fetch1099Years = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/me/1099/years`);
      const years = data.years || [];
      setTaxYears(years);
      // Default to the most recent year with earnings, else current year.
      if (years.length > 0 && !years.some(y => y.year === selectedTaxYear)) {
        setSelectedTaxYear(years[0].year);
      }
    } catch (e) {
      // non-fatal — driver might not have any completed trips yet
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const downloadAnnualSummary = async (format = "csv", yearOverride) => {
    const year = yearOverride || selectedTaxYear || new Date().getFullYear();
    try {
      const url = `${API}/me/1099/${format}?year=${year}`;
      // Use axios with token for auth, get blob back
      const res = await axios.get(url, { responseType: "blob" });
      const blob = res.data;
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `Link Ride-driver-1099-${year}.${format === "pdf" ? "pdf" : "csv"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success(`${year} ${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const fetchEarnings = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [earningsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/driver/earnings?period=${period}`),
        axios.get(`${API}/driver/completed-jobs?period=${period}`)
      ]);
      setEarnings(earningsRes.data);
      // Sort newest-first so the most recent completed jobs surface at the top
      const jobs = Array.isArray(jobsRes.data) ? [...jobsRes.data] : [];
      jobs.sort((a, b) => {
        const ta = a.completed_at ? new Date(a.completed_at).getTime() : 0;
        const tb = b.completed_at ? new Date(b.completed_at).getTime() : 0;
        return tb - ta;
      });
      setCompletedJobs(jobs);
    } catch (err) {
      console.error("Failed to fetch earnings:", err);
      toast.error("Failed to load earnings data");
    } finally {
      setLoading(false);
    }
  }, [user, period]);

  const fetchDeliveries = useCallback(async () => {
    if (!user) return;
    try {
      const [availableRes, myRes] = await Promise.all([
        axios.get(`${API}/driver/available-deliveries`),
        axios.get(`${API}/driver/my-deliveries`)
      ]);
      // Sort available deliveries by pickup time (soonest first — that's
      // the order you'd actually work them) and my-deliveries the same way
      // so the driver's active queue reads top-to-bottom in workflow order.
      const byPickup = (arr) => (Array.isArray(arr) ? [...arr] : []).sort((a, b) => {
        const ts = (b) => {
          if (b.scheduled_date) {
            const d = new Date(`${b.scheduled_date}T${b.scheduled_time || "00:00"}`);
            if (!isNaN(d.getTime())) return d.getTime();
          }
          if (b.created_at) return new Date(b.created_at).getTime() || 0;
          return 0;
        };
        return ts(a) - ts(b);
      });
      setAvailableDeliveries(byPickup(availableRes.data));
      setMyDeliveries(byPickup(myRes.data));
    } catch (err) {
      console.error("Failed to fetch deliveries:", err);
    }
  }, [user]);

  useEffect(() => {
    // Admins share the same driver bottom-nav as drivers do (sole-operator
    // setup where the owner is also the primary driver). They should be
    // able to see the earnings dashboard just like any driver — previously
    // this gate bounced them to "/" as soon as they tapped the tab.
    if (user && (isDriver || isAdmin)) {
      fetchEarnings();
      fetchDeliveries();
      fetch1099Years();
    } else if (user && !isDriver && !isAdmin) {
      navigate("/");
    }
  }, [user, isDriver, isAdmin, fetchEarnings, fetchDeliveries, fetch1099Years, navigate]);

  const acceptDelivery = async (bookingId) => {
    try {
      await axios.post(`${API}/driver/accept-booking/${bookingId}`);
      toast.success("Delivery accepted!");
      fetchDeliveries();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept delivery");
    }
  };

  const completeDelivery = async (bookingId) => {
    try {
      await axios.post(`${API}/driver/complete-booking/${bookingId}`);
      toast.success("Delivery completed!");
      fetchDeliveries();
      fetchEarnings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete delivery");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-5">
        <Card className="bg-[#121212] border-white/5 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-[#4D88FF] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Login Required</h2>
            <p className="text-zinc-400 mb-4">Please sign in to view your earnings</p>
            <Button 
              onClick={() => navigate("/login?redirect=/driver/earnings")}
              className="bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-xl border-0"
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getPeriodLabel = () => {
    switch (period) {
      case 'day': return "Today";
      case 'week': return "This Week";
      case 'month': return "This Month";
      case 'all': return "All Time";
      default: return "This Week";
    }
  };

  const getServiceInfo = (type) => {
    return SERVICE_LABELS[type] || SERVICE_LABELS.delivery;
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-[#050505]">
      {/* Header */}
      <header className="flex-shrink-0 z-10 bg-[#050505]/95 backdrop-blur border-b border-white/5">
        <div className="px-5 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/driver")}
            className="text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-white">Earnings & Deliveries</h1>
            <p className="text-sm text-zinc-500">{user.preferred_name || user.first_name} {user.last_name}</p>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="px-5 pb-3">
          <TabsList className="w-full bg-[#121212] border border-white/5">
            <TabsTrigger value="earnings" className="flex-1 data-[state=active]:bg-[#0047FF] data-[state=active]:text-black">
              Earnings
            </TabsTrigger>
            <TabsTrigger value="deliveries" className="flex-1 data-[state=active]:bg-[#0047FF] data-[state=active]:text-black">
              Deliveries
              {availableDeliveries.length > 0 && (
                <Badge className="ml-2 bg-emerald-500 text-white text-xs">{availableDeliveries.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </header>

      {activeTab === "earnings" && (
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Period Selector */}
          <div className="flex-shrink-0 px-5 py-3 flex gap-2 overflow-x-auto border-b border-white/5">
            {[
              { value: 'day', label: 'Today' },
              { value: 'week', label: 'Week' },
              { value: 'month', label: 'Month' },
              { value: 'all', label: 'All' }
            ].map(p => (
              <Button
                key={p.value}
                variant={period === p.value ? "default" : "outline"}
                size="sm"
                onClick={() => setPeriod(p.value)}
                className={period === p.value 
                  ? "bg-[#0047FF] text-white hover:bg-[#003BE0]" 
                  : "border-white/10 text-zinc-400 hover:bg-zinc-800"
                }
              >
                {p.label}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadAnnualSummary("csv")}
              data-testid="download-1099-csv-quick"
              className="ml-auto border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 whitespace-nowrap"
              title="Download CSV summary for your accountant. Stripe auto-files Form 1099-NEC if you cross $600."
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> {selectedTaxYear} 1099 CSV
            </Button>
          </div>

          {loading ? (
            <div className="flex-1 min-h-0 flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#4D88FF] animate-spin" />
            </div>
          ) : (
            <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-6 pb-24 max-w-lg mx-auto w-full space-y-6">
              {/* Earnings Summary Card */}
              <Card className="bg-gradient-to-br from-[#0047FF]/20 to-[#0047FF]/5 border-[#0047FF]/30">
                <CardContent className="p-6">
                  <div className="text-center">
                    <p className="text-sm text-[#4D88FF]/80 mb-1">{getPeriodLabel()} Earnings</p>
                    <p className="text-4xl font-bold text-white mb-2">
                      {formatCurrency(earnings?.totalEarnings)}
                    </p>
                    <div className="flex items-center justify-center gap-1 text-emerald-400 text-sm">
                      <TrendingUp className="w-4 h-4" />
                      <span>{earnings?.completedTrips || 0} deliveries completed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3">
                <Card className="bg-[#121212] border-white/5">
                  <CardContent className="p-4 text-center">
                    <Package className="w-5 h-5 text-[#4D88FF] mx-auto mb-2" />
                    <p className="text-lg font-bold text-white">{earnings?.completedTrips || 0}</p>
                    <p className="text-xs text-zinc-500">Deliveries</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#121212] border-white/5">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-5 h-5 text-[#4D88FF] mx-auto mb-2" />
                    <p className="text-lg font-bold text-white">{earnings?.totalHours || 0}h</p>
                    <p className="text-xs text-zinc-500">Active</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#121212] border-white/5">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="w-5 h-5 text-[#4D88FF] mx-auto mb-2" />
                    <p className="text-lg font-bold text-white">{formatCurrency(earnings?.avgPerTrip)}</p>
                    <p className="text-xs text-zinc-500">Avg/Delivery</p>
                  </CardContent>
                </Card>
              </div>

              {/* Service Type Breakdown */}
              {earnings?.serviceBreakdown && Object.keys(earnings.serviceBreakdown).length > 0 && (
                <Card className="bg-[#121212] border-white/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-zinc-400">Earnings by Service</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Object.entries(earnings.serviceBreakdown).map(([type, data]) => {
                      const svc = getServiceInfo(type);
                      const IconComponent = svc.icon;
                      return (
                        <div key={type} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${svc.bg}`}>
                              <IconComponent className={`w-4 h-4 ${svc.color}`} />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{svc.name}</p>
                              <p className="text-xs text-zinc-500">{data.count} completed</p>
                            </div>
                          </div>
                          <p className={`font-bold ${svc.color}`}>{formatCurrency(data.earnings)}</p>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Earnings Breakdown */}
              <Card className="bg-[#121212] border-white/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-zinc-400">Earnings Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-zinc-400">Delivery Earnings</span>
                    <span className="text-white font-medium">{formatCurrency(earnings?.tripEarnings)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-zinc-400">Tips</span>
                    <span className="text-white font-medium">{formatCurrency(earnings?.tips)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/5">
                    <span className="text-zinc-400">Bonuses</span>
                    <span className="text-emerald-400 font-medium">{formatCurrency(earnings?.bonuses)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-white font-medium">Total</span>
                    <span className="text-[#4D88FF] font-bold text-lg">{formatCurrency(earnings?.totalEarnings)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Annual 1099 Statement — year picker + PDF/CSV downloads */}
              <Card className="bg-[#121212] border-white/5" data-testid="annual-1099-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-emerald-400" /> Annual 1099 Statement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Download a 1099-NEC-style PDF or detailed CSV for any tax year you drove with Link Ride. Hand the PDF to your accountant; keep the CSV with your records.
                    {" "}
                    <span className="text-zinc-400">Stripe Express auto-files the official IRS Form 1099-NEC when annual payouts ≥ $600.</span>
                  </p>

                  {taxYears.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic py-2" data-testid="annual-1099-empty">
                      No completed trips on file yet — your 1099 statement will appear here once you start earning.
                    </p>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-2" data-testid="annual-1099-years">
                        {taxYears.map((y) => (
                          <button
                            key={y.year}
                            type="button"
                            onClick={() => setSelectedTaxYear(y.year)}
                            data-testid={`tax-year-${y.year}`}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                              selectedTaxYear === y.year
                                ? "bg-emerald-500 text-black"
                                : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                            }`}
                          >
                            {y.year}
                            <span className="ml-1.5 opacity-70">· ${y.earnings.toFixed(0)}</span>
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => downloadAnnualSummary("pdf")}
                          className="flex-1 bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-xl border-0"
                          data-testid="download-1099-pdf"
                        >
                          <Download className="w-4 h-4 mr-2" /> {selectedTaxYear} PDF
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => downloadAnnualSummary("csv")}
                          className="flex-1 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-xl"
                          data-testid="download-1099-csv"
                        >
                          <Download className="w-4 h-4 mr-2" /> {selectedTaxYear} CSV
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Driver Tax Center — mileage + expenses + IRS Std vs Actual comparison */}
              <DriverTaxCenter year={selectedTaxYear} />

              {/* Payout Status */}
              <Card className="bg-[#121212] border-white/5">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-zinc-400">Payout Status</CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-[#4D88FF] hover:text-[#7AADFF]"
                      onClick={() => navigate("/driver/profile")}
                    >
                      <CreditCard className="w-4 h-4 mr-1" />
                      Bank
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {earnings?.bankConnected ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                          <div>
                            <p className="text-sm font-medium text-white">Bank Connected</p>
                            <p className="text-xs text-zinc-400">Payouts enabled</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-400">Available for payout</span>
                        <span className="text-emerald-400 font-bold">{formatCurrency(earnings?.availableBalance)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-400">Next payout</span>
                        <span className="text-zinc-300">{earnings?.nextPayoutDate || "Processing"}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <AlertCircle className="w-8 h-8 text-[#4D88FF] mx-auto mb-2" />
                      <p className="text-sm text-zinc-400 mb-3">Connect your bank to receive payouts</p>
                      <Button
                        onClick={() => navigate("/driver/profile")}
                        className="bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-xl border-0"
                      >
                        Connect Bank
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Trip History */}
              <Card className="bg-[#121212] border-white/5">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-zinc-400">Recent Deliveries</CardTitle>
                    <span className="text-xs text-zinc-500">{completedJobs.length} total</span>
                  </div>
                </CardHeader>
                <CardContent>
                  {completedJobs.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-500">No completed deliveries yet</p>
                      <p className="text-xs text-zinc-600 mt-1">Accept deliveries to see them here</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {completedJobs.slice(0, 10).map((job, index) => {
                        const svc = getServiceInfo(job.service_type);
                        const IconComponent = svc.icon;
                        return (
                          <div 
                            key={job.id || index} 
                            className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${svc.bg}`}>
                                <IconComponent className={`w-4 h-4 ${svc.color}`} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white">{job.customer_name}</p>
                                <p className="text-xs text-zinc-500">
                                  {svc.name} • {formatDate(job.completed_at)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-emerald-400">{formatCurrency(job.total_payout)}</p>
                              {job.tip > 0 && (
                                <p className="text-xs text-zinc-500">+{formatCurrency(job.tip)} tip</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Weekly Summary */}
              {earnings?.weeklyBreakdown && (
                <Card className="bg-[#121212] border-white/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-zinc-400">Weekly Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {earnings.weeklyBreakdown.map((day, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <span className="text-xs text-zinc-500 w-8">{day.day}</span>
                          <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#0047FF] rounded-full"
                              style={{ width: `${(day.amount / (earnings.maxDailyEarning || 100)) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-zinc-400 w-16 text-right">{formatCurrency(day.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </main>
          )}
        </div>
      )}

      {activeTab === "deliveries" && (
        <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-6 pb-24 max-w-lg mx-auto w-full space-y-6">
          {/* Available Deliveries */}
          <Card className="bg-[#121212] border-white/5">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-white">Available Deliveries</CardTitle>
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                  {availableDeliveries.length} available
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {availableDeliveries.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">No deliveries available</p>
                  <p className="text-xs text-zinc-600 mt-1">Check back later for new requests</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableDeliveries.map((delivery) => {
                    const svc = getServiceInfo(delivery.service_type);
                    const IconComponent = svc.icon;
                    return (
                      <div key={delivery.id} className="p-4 rounded-lg bg-zinc-800/50 border border-white/10" data-testid={`avail-delivery-${delivery.id}`}>
                        <div className="flex items-start justify-between mb-3 gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`p-2 rounded-lg ${svc.bg} shrink-0`}>
                              <IconComponent className={`w-5 h-5 ${svc.color}`} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge className={`${svc.bg} ${svc.color} border-white/10 text-[10px] font-bold uppercase`} data-testid={`avail-service-badge-${delivery.id}`}>
                                  {svc.name}
                                </Badge>
                                {delivery.asap && (
                                  <Badge className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30 text-[10px] font-bold uppercase">ASAP</Badge>
                                )}
                              </div>
                              <p className="font-medium text-white truncate mt-1">{delivery.customer_name}</p>
                              <p className="text-[10px] text-zinc-500 font-mono">#{(delivery.id || '').slice(0, 8)}</p>
                            </div>
                          </div>
                          <p className="text-lg font-bold text-emerald-400 shrink-0">
                            {formatCurrency(delivery.driver_payout)}
                          </p>
                        </div>
                        <div className="space-y-2 mb-3">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                            <p className="text-sm text-zinc-300">{delivery.pickup_address}</p>
                          </div>
                          {Array.isArray(delivery.stops) && delivery.stops.length > 0 && delivery.stops.map((stop, idx) => (
                            <div key={idx} className="flex items-start gap-2" data-testid={`avail-stop-${idx}`}>
                              <MapPin className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                              <p className="text-sm text-amber-300"><span className="font-semibold">Stop {idx + 1}:</span> {stop.address}</p>
                            </div>
                          ))}
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-[#4D88FF] mt-0.5 shrink-0" />
                            <p className="text-sm text-zinc-300">{delivery.dropoff_address}</p>
                          </div>
                          {delivery.notes && (
                            <p className="text-xs text-zinc-500 italic pl-6">"{delivery.notes}"</p>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-3 text-xs text-zinc-500">
                            {typeof delivery.distance_miles === 'number' && (
                              <span>{delivery.distance_miles.toFixed(1)} mi</span>
                            )}
                            {delivery.scheduled_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> {delivery.scheduled_date}
                              </span>
                            )}
                            {delivery.scheduled_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {delivery.scheduled_time}
                              </span>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => acceptDelivery(delivery.id)}
                            className="bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-xl border-0"
                            data-testid={`accept-delivery-${delivery.id}`}
                          >
                            Accept
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* My Active Deliveries */}
          <Card className="bg-[#121212] border-white/5">
            <CardHeader>
              <CardTitle className="text-lg text-white">My Deliveries</CardTitle>
            </CardHeader>
            <CardContent>
              {myDeliveries.filter(d => d.status === "in_progress").length === 0 ? (
                <div className="text-center py-6">
                  <Car className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">No active deliveries</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {myDeliveries.filter(d => d.status === "in_progress").map((delivery) => {
                    const svc = getServiceInfo(delivery.service_type);
                    const IconComponent = svc.icon;
                    return (
                      <div key={delivery.id} className="p-4 rounded-lg bg-[#0047FF]/10 border border-[#0047FF]/30" data-testid={`my-delivery-${delivery.id}`}>
                        <div className="flex items-start justify-between mb-3 gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`p-2 rounded-lg ${svc.bg} shrink-0`}>
                              <IconComponent className={`w-5 h-5 ${svc.color}`} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge className={`${svc.bg} ${svc.color} border-white/10 text-[10px] font-bold uppercase`} data-testid={`my-service-badge-${delivery.id}`}>
                                  {svc.name}
                                </Badge>
                                <Badge className="bg-[#0047FF]/20 text-[#4D88FF] border-[#0047FF]/30 text-[10px] font-bold uppercase">
                                  In Progress
                                </Badge>
                              </div>
                              <p className="font-medium text-white truncate mt-1">{delivery.customer_name}</p>
                              <p className="text-[10px] text-zinc-500 font-mono">#{(delivery.id || '').slice(0, 8)}</p>
                            </div>
                          </div>
                          <p className="text-lg font-bold text-emerald-400 shrink-0">
                            {formatCurrency(delivery.driver_payout)}
                          </p>
                        </div>
                        <div className="space-y-2 mb-3">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                            <p className="text-sm text-zinc-300">{delivery.pickup_address}</p>
                          </div>
                          {Array.isArray(delivery.stops) && delivery.stops.length > 0 && delivery.stops.map((stop, idx) => (
                            <div key={idx} className="flex items-start gap-2" data-testid={`active-stop-${idx}`}>
                              <MapPin className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                              <p className="text-sm text-amber-300"><span className="font-semibold">Stop {idx + 1}:</span> {stop.address}</p>
                            </div>
                          ))}
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-[#4D88FF] mt-0.5 shrink-0" />
                            <p className="text-sm text-zinc-300">{delivery.dropoff_address}</p>
                          </div>
                          {delivery.notes && (
                            <p className="text-xs text-zinc-500 italic pl-6">"{delivery.notes}"</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-zinc-500 pl-6 pt-1">
                            {typeof delivery.distance_miles === 'number' && (
                              <span>{delivery.distance_miles.toFixed(1)} mi</span>
                            )}
                            {delivery.scheduled_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> {delivery.scheduled_date}
                              </span>
                            )}
                            {delivery.scheduled_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {delivery.scheduled_time}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          onClick={() => completeDelivery(delivery.id)}
                          className="w-full bg-emerald-500 hover:bg-emerald-400 text-black"
                          data-testid={`complete-delivery-${delivery.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark Complete
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      )}
      <DriverBottomNav />
    </div>
  );
}
